import React, { useState, useEffect } from 'react';
import { Input, Select, Button, message, Form } from 'antd';
import { Dictionary } from '@onaio/utils';
import { useDispatch, useStore } from 'react-redux';
import { CheckOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { actionSourceSave, actionSourceDimensionDelete } from '../../actions';
import {
  transformSourceUIDimensionsAndMeasuresToCubeFormat,
  updateSourceDimensions,
  updateSourceCubeSchema,
  generateAutoMeasuresForDimension,
  removeAutoMeasuresForDimension,
  deleteSourceCubeDimensions
} from '../../helpers/helpers';
import { TEMPORAL_SOURCES_WORKER_WEB_SOCKET_URL } from '../../../../configs/env';
import { triggerAsycSourceOperation } from '../SourceWebsocketsUpdatesHelpers';
import { Source, defaultProgressStatusTypesForAsyncTasks } from '../../../../configs/component-types';

const { Option } = Select;

export interface DimensionHeaderFormProps {
  item: Dictionary;
  index: number;
  cubePropertyDeletionData: any;
  deletingCubeProperty: boolean;
  setCubePropertyDeletionData: (data: any) => void;
  setDeletingCubeProperty: (loading: boolean) => void;
  setDeleteProgress: (progress: any) => void;
  source: Dictionary;
  dispatch: any;
  sourceActionSaveCreator?: (obj: Source) => void;
}

const defaultProps = {
  sourceActionSaveCreator: actionSourceSave,
};

const CubePropertiesTypes = {
  DIMENSION: 'Dimension',
  MEASURE: 'Measure',
};

const DimensionHeaderForm: React.FC<DimensionHeaderFormProps> = (props: DimensionHeaderFormProps) => {
  const {
    item,
    index,
    cubePropertyDeletionData,
    deletingCubeProperty,
    setCubePropertyDeletionData,
    setDeletingCubeProperty,
    setDeleteProgress,
    source,
    dispatch,
    sourceActionSaveCreator
  } = props;

  const store = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editValues, setEditValues] = useState({
    value: item.value || '',
    title: item.title || '',
    type: item.type || 'string'
  });

  const [originalValues, setOriginalValues] = useState({
    value: item.value || '',
    title: item.title || '',
    type: item.type || 'string'
  });

  const hasChanges = editValues.value !== originalValues.value ||
    editValues.title !== originalValues.title ||
    editValues.type !== originalValues.type;
  const [progress, setProgress] = useState<
    | { stage: defaultProgressStatusTypesForAsyncTasks; data: Dictionary[]; errors: Dictionary[] }
    | Dictionary
  >({});

  const currentState = store.getState();
  /* @ts-ignore */
  const sourceObj = Object.assign({}, currentState.source);

  // Handle progress updates from websocket operations
  useEffect(() => {
    if (progress?.stage === defaultProgressStatusTypesForAsyncTasks.DONE) {
      if (progress?.data[0]?.source) {
        if (sourceActionSaveCreator) {
          dispatch(
            /* @ts-ignore */
            sourceActionSaveCreator({
              ...progress?.data[0]?.source,
            })
          );
        }
      }
      setIsSubmitting(false);
      setProgress({});
      
      // Update original values to reflect the saved state
      setOriginalValues({
        value: editValues.value,
        title: editValues.title,
        type: editValues.type
      });

      const oldType = item.type;
      const newType = editValues.type;
      const typeChanged = oldType !== newType;
      const shouldGenerateAutoMeasures = typeChanged && oldType === 'string' && newType === 'number';
      const shouldRemoveAutoMeasures = typeChanged && oldType === 'number' && (newType === 'string' || newType === 'time');

      if (shouldGenerateAutoMeasures) {
        message.success(`Dimension "${editValues.value}" updated and auto-measures generated`);
      } else if (shouldRemoveAutoMeasures) {
        message.success(`Dimension "${editValues.value}" updated and auto-measures removed`);
      } else {
        message.success(`Dimension "${editValues.value}" updated successfully`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  const handleSave = async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (!editValues.value.trim()) {
      message.error('Dimension name is required');
      return;
    }

    if (Number(editValues.value.substring(0, 1))) {
      message.error('Dimension names must begin with a letter.');
      return;
    }

    if (editValues.value.includes(' ')) {
      message.error('No spaces are allowed in Dimension names. Use underscores (_) instead.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if type changed for auto-measure handling
      const oldType = item.type;
      const newType = editValues.type;
      const typeChanged = oldType !== newType;
      const shouldGenerateAutoMeasures = typeChanged && oldType === 'string' && newType === 'number';
      const shouldRemoveAutoMeasures = typeChanged && oldType === 'number' && (newType === 'string' || newType === 'time');

      // Update dimension first, then handle measures if needed
      const updatedDimension = {
        ...item,
        ...editValues
      };

      // If we need to handle auto-measures, we'll do everything in one schema update
      if (shouldGenerateAutoMeasures || shouldRemoveAutoMeasures) {
        // Get current schema and create updated version
        let currentSchema = { ...sourceObj.schema };
        let currentMeasures = currentSchema.measures || [];

        if (shouldGenerateAutoMeasures) {
          // Check if auto-measures already exist for this dimension
          const existingAutoMeasures = currentMeasures.some((measureObj: any) => {
            if (!measureObj || typeof measureObj !== 'object') return false;
            const measureNames = Object.keys(measureObj);
            return measureNames.some(name =>
              name.startsWith(`${editValues.value}__`) &&
              measureObj[name]?.meta?.autoGenerated === true
            );
          });

          if (!existingAutoMeasures) {
            // Also check if manual measures exist for sum, avg, min, max
            const hasManualMeasures = currentMeasures.some((measureObj: any) => {
              if (!measureObj || typeof measureObj !== 'object') return false;
              const measureNames = Object.keys(measureObj);
              return measureNames.some(name =>
                (name.includes('sum') || name.includes('avg') || name.includes('min') || name.includes('max')) &&
                measureObj[name]?.sql === editValues.value
              );
            });

            if (!hasManualMeasures) {
              // Generate auto-measures for this specific dimension
              const autoMeasures = generateAutoMeasuresForDimension(editValues.value, editValues.title);

              if (currentMeasures.length === 0) {
                currentMeasures = [autoMeasures];
              } else {
                const updatedObject = { ...currentMeasures[0], ...autoMeasures };
                const newList = [updatedObject, ...currentMeasures.slice(1)];
                currentMeasures = [...newList];
              }
            }
          }
        } else if (shouldRemoveAutoMeasures) {
          // Remove auto-measures for this specific dimension
          currentMeasures = removeAutoMeasuresForDimension(currentMeasures, editValues.value);
        }

        // Update dimensions in schema
        let currentDimensions = currentSchema.dimensions || [];
        if (currentDimensions.length > 0) {
          // Update existing dimension in schema
          const schemaDimensions = { ...currentDimensions[0] };
          schemaDimensions[editValues.value] = {
            ...schemaDimensions[editValues.value],
            title: editValues.title,
            type: editValues.type,
          };
          currentDimensions = [schemaDimensions];
        }

        // Create complete updated schema
        const updatedSchema = {
          ...currentSchema,
          measures: currentMeasures,
          dimensions: currentDimensions
        };

        // Preserve required properties
        if (sourceObj?.schema?.sql) {
          updatedSchema.sql = sourceObj.schema.sql;
        }
        if (sourceObj?.schema?.sql_table) {
          updatedSchema.sql_table = sourceObj.schema.sql_table;
        }
        if (sourceObj?.schema?.refreshKey) {
          updatedSchema.refreshKey = sourceObj.schema.refreshKey;
        }

        // Update entire schema (dimension + measures) in one operation
        await triggerAsycSourceOperation({
          asynFunc: updateSourceCubeSchema,
          asynFuncArgs: {
            sourceId: sourceObj?.uuid,
            schema: updatedSchema,
            cubeName: sourceObj?.cube,
          },
          asyncTaskName: `Updating dimension ${editValues.value}${shouldGenerateAutoMeasures ? ' and generating auto-measures' : shouldRemoveAutoMeasures ? ' and removing auto-measures' : ''}`,
          setAsyncTaskProgressData: setProgress,
          setLoading: setIsSubmitting,
          webSocketUrl: TEMPORAL_SOURCES_WORKER_WEB_SOCKET_URL,
        });

      } else {
        // Just update the dimension without measure changes
        const { dimensions: cubeDimensions } = transformSourceUIDimensionsAndMeasuresToCubeFormat({
          dimensions: [updatedDimension],
          measures: [],
          cubeDimensions: [],
          cubeMeasures: [],
        });

        await triggerAsycSourceOperation({
          asynFunc: updateSourceDimensions,
          asynFuncArgs: {
            sourceId: sourceObj?.uuid,
            dimensions: cubeDimensions,
            cubeName: sourceObj?.cube,
          },
          asyncTaskName: `Updating dimension ${editValues.value}`,
          setAsyncTaskProgressData: setProgress,
          setLoading: setIsSubmitting,
          webSocketUrl: TEMPORAL_SOURCES_WORKER_WEB_SOCKET_URL,
        });
      }

    } catch (error) {
      message.error('Failed to update dimension');
      console.error('Error updating dimension:', error);
      setIsSubmitting(false);
    }
  };


  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        padding: '4px 0'
      }}
    >
      <Input
        size="middle"
        value={editValues.value}
        onChange={(e) => setEditValues({ ...editValues, value: e.target.value })}
        placeholder="Dimension name"
        style={{ width: '140px' }}
        disabled={isSubmitting || item.value ? true : false}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
      />

      <Input
        size="middle"
        value={editValues.title}
        onChange={(e) => setEditValues({ ...editValues, title: e.target.value })}
        placeholder="Label"
        style={{ width: '140px' }}
        disabled={isSubmitting || source.public}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
      />

      <Select
        size="middle"
        value={editValues.type}
        onChange={(value) => setEditValues({ ...editValues, type: value })}
        style={{ width: '100px' }}
        disabled={isSubmitting || source.public}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
      >
        <Option value="string">String</Option>
        <Option value="number">Number</Option>
        <Option value="time">Time</Option>
      </Select>

      {item.custom && (
        <span style={{
          padding: '2px 6px',
          backgroundColor: '#f6ffed',
          color: '#52c41a',
          fontSize: '10px',
          borderRadius: '2px',
          fontWeight: 'normal'
        }}>
          CUSTOM
        </span>
      )}

      {hasChanges && (
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSave}
          loading={isSubmitting}
          disabled={source.public}
          style={{ minWidth: '32px' }}
        />
      )}
    </div>
  );
};

DimensionHeaderForm.defaultProps = defaultProps;

export { DimensionHeaderForm };